/**
 * La direzione del sentiment giorno per giorno, un binario per ambito e uno
 * per il confronto (F-177).
 *
 * I tre binari stanno **uno sopra l'altro sulla stessa griglia di giorni**, e
 * con le stesse gronde dei grafici sotto: la domanda della pagina è «borsa e
 * crypto andavano insieme?», e a quella si risponde con un colpo d'occhio
 * verticale, non confrontando due liste.
 *
 * Come `JudgementTimeline`, gli stati sono più di due e restano distinti anche
 * senza colore:
 *
 *  - **letto** — la direzione, a colore pieno; la confidenza bassa è più tenue;
 *  - **fallito** — c'era una lettura, il modello non ha dato un esito: righe
 *    oblique. Si indaga;
 *  - **nessuna lettura** — non era prevista (automatica spenta, o prima che
 *    esistesse): bordo tratteggiato. Si spiega.
 */

import { formatDate } from '@/lib/format';
import { CHART_GUTTERS, daysBetween } from './briefing-history';
import type { SentimentDivergence, SentimentHistoryPoint } from './sentiment-api';
import { DIRECTION_LABEL, DIVERGENCE_LABEL, signedScore } from './sentiment-format';
import './briefing-history.css';
import './sentiment-history.css';

type Props = {
  from: string;
  to: string;
  stock: readonly SentimentHistoryPoint[];
  crypto: readonly SentimentHistoryPoint[];
  comparisons: ReadonlyArray<{ date: string; divergence: SentimentDivergence }>;
  hoveredDate: string | null;
  onHover: (date: string | null) => void;
};

function describe(label: string, date: string, point: SentimentHistoryPoint | undefined): string {
  if (!point) return `${label}, ${formatDate(date)}: nessuna lettura`;
  if (point.status === 'failed' || !point.direction) return `${label}, ${formatDate(date)}: lettura fallita`;
  const revised = point.revisionCount > 1 ? ` (${point.revisionCount} letture in giornata)` : '';
  return `${label}, ${formatDate(date)}: ${DIRECTION_LABEL[point.direction]} ${signedScore(point.score)}, confidenza ${point.confidence ?? 'n/d'}${revised}`;
}

function DomainTrack({
  label,
  days,
  points,
  hoveredDate,
  onHover,
}: {
  label: string;
  days: readonly string[];
  points: readonly SentimentHistoryPoint[];
  hoveredDate: string | null;
  onHover: (date: string | null) => void;
}) {
  const byDate = new Map(points.map((point) => [point.date, point]));
  return (
    <div className="sentiment-timeline__row">
      <span className="sentiment-timeline__label" style={{ flexBasis: CHART_GUTTERS.left }}>
        {label}
      </span>
      <ol className="timeline__track" aria-label={`Direzione ${label}`}>
        {days.map((date) => {
          const point = byDate.get(date);
          const state = !point ? 'absent' : point.status === 'failed' || !point.direction ? 'failed' : 'read';
          const text = describe(label, date, point);
          return (
            <li
              key={date}
              className={[
                'timeline__day',
                'sentiment-timeline__day',
                `sentiment-timeline__day--${state}`,
                point?.direction ? `is-${point.direction}` : '',
                point?.confidence === 'bassa' ? 'is-low-confidence' : '',
                hoveredDate === date ? 'is-hovered' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              title={text}
              onMouseEnter={() => onHover(date)}
            >
              <span className="visually-hidden">{text}</span>
              {point && point.revisionCount > 1 && <span className="timeline__revised" aria-hidden="true" />}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function SentimentTimeline({ from, to, stock, crypto, comparisons, hoveredDate, onHover }: Props) {
  const days = daysBetween(from, to);
  const divergenceByDate = new Map(comparisons.map((entry) => [entry.date, entry.divergence]));

  return (
    <div className="sentiment-timeline" style={{ paddingRight: CHART_GUTTERS.right }} onMouseLeave={() => onHover(null)}>
      <DomainTrack label="Borsa" days={days} points={stock} hoveredDate={hoveredDate} onHover={onHover} />
      <DomainTrack label="Crypto" days={days} points={crypto} hoveredDate={hoveredDate} onHover={onHover} />

      {/* Il confronto è un binario più basso: deriva dai due sopra, non è una
          terza misura, e non deve pesare quanto loro. */}
      <div className="sentiment-timeline__row sentiment-timeline__row--compare">
        <span className="sentiment-timeline__label" style={{ flexBasis: CHART_GUTTERS.left }}>
          Confronto
        </span>
        <ol className="timeline__track" aria-label="Confronto fra i due ambiti">
          {days.map((date) => {
            const divergence = divergenceByDate.get(date);
            const text = divergence
              ? `${formatDate(date)}: ${DIVERGENCE_LABEL[divergence]}`
              : `${formatDate(date)}: manca l'esito di almeno un ambito`;
            return (
              <li
                key={date}
                className={`timeline__day sentiment-timeline__compare${divergence ? ` is-${divergence}` : ''}${
                  hoveredDate === date ? ' is-hovered' : ''
                }`}
                title={text}
                onMouseEnter={() => onHover(date)}
              >
                <span className="visually-hidden">{text}</span>
              </li>
            );
          })}
        </ol>
      </div>

      <ul className="timeline__legend sentiment-timeline__legend" style={{ paddingLeft: CHART_GUTTERS.left }}>
        <li className="timeline__legend-item is-risk_on">propensione al rischio</li>
        <li className="timeline__legend-item is-risk_off">avversione al rischio</li>
        <li className="timeline__legend-item is-neutral">piatto</li>
        <li className="timeline__legend-item is-mixed">segnali contrastanti</li>
        <li className="timeline__legend-item sentiment-timeline__legend--failed">lettura fallita</li>
        <li className="timeline__legend-item sentiment-timeline__legend--absent">nessuna lettura</li>
        <li className="timeline__legend-item is-opposed">contrapposti</li>
        <li className="timeline__legend-item is-aligned">di pari passo</li>
      </ul>
    </div>
  );
}
